import { CurriculumAdapter, LevelLite, LevelStatus } from './types';
// Activity images for AI Fall 2025 (place files under shared/assets/images/activities/ai-fall-2025/)
import MuseL1Img from '../../../shared/assets/images/activities/ai-fall-2025/World Level 1 - Create Muse image.jpg';
import MuseL2Img from '../../../shared/assets/images/activities/ai-fall-2025/World Level 2 - Dress to Impress.jpg';
import MuseL3Img from '../../../shared/assets/images/activities/ai-fall-2025/Muse Level 3 - Secret Item.jpg';
import WorldL1Img from '../../../shared/assets/images/activities/ai-fall-2025/World Level 1 - Create world.jpg';
import WorldL2Img from '../../../shared/assets/images/activities/ai-fall-2025/World Level 2 - add plants and animals.jpg';
import WorldL3Img from '../../../shared/assets/images/activities/ai-fall-2025/World Level 3- Add buildings.png';
import WorldL4Img from '../../../shared/assets/images/activities/ai-fall-2025/World Level 4 Make a Friend.jpg';
// RealOrAI game thumbnail
import RealOrAiThumb from '../../../shared/assets/images/games/Real Or AI Thumbnail.png';

// New curriculum: ai-fall-2025 (5 classes = 5 levels)
// Default thumbnails/images can be added later; keeping minimal level shapes.

const LEVELS: LevelLite[] = [
  {
    id: '1',
    title: 'Muses & Worlds',
    missions: [
      { id: 'welcome-overview', type: 'completion', text: 'Welcome & overview: safety workflow and goals' },
      { id: 'realorai-l1', type: 'measurable', text: 'Play RealOrAI (Muse: Level 1)', gameId: 'real-or-ai', target: 1 },
      { id: 'muse-image-l1', type: 'completion', text: 'Create Muse image (Muse: Level 1)' },
      { id: 'muse-accessories-l2', type: 'completion', text: 'Dress to Impress (Muse: Level 2)' },
      { id: 'muse-secret-items-l3', type: 'completion', text: 'Secret items (Muse: Level 3)' },
      { id: 'world-base-l1', type: 'completion', text: 'Create base World image (World: Level 1)' },
      { id: 'world-flora-fauna-l2', type: 'completion', text: 'Add plants & animals (World: Level 2)' },
      { id: 'world-buildings-l3', type: 'completion', text: 'Add buildings (World: Level 3)' },
      { id: 'world-collab-l4', type: 'completion', text: 'Make a Friend (World: Level 4)' },
    ],
  },
  {
    id: '2',
    title: 'Create a Story',
    missions: [
      { id: 'storyboard-core-parts', type: 'completion', text: 'Build a storyboard with core story parts' },
      { id: 'story-short-draft', type: 'completion', text: 'Write a short story draft' },
      { id: 'story-edit-revision', type: 'completion', text: 'Revise after feedback' },
    ],
  },
  {
    id: '3',
    title: 'Create a Scene',
    missions: [
      { id: 'scene-dialogue-written', type: 'completion', text: 'Write dialogue for two characters' },
      { id: 'scene-voices-elevenlabs', type: 'completion', text: 'Generate voices in ElevenLabs' },
      { id: 'scene-iteration-improvements', type: 'completion', text: 'Iterate on clarity and delivery' },
    ],
  },
  {
    id: '4',
    title: 'Create a Music Video',
    missions: [
      { id: 'video-prompt-ready', type: 'completion', text: 'Prepare video prompts (mood, motion, pacing)' },
      { id: 'video-generated', type: 'completion', text: 'Generate a short music video' },
    ],
  },
  {
    id: '5',
    title: 'Showcase and Celebration',
    missions: [
      { id: 'portfolio-assembled', type: 'completion', text: 'Assemble your portfolio' },
      { id: 'showcase-presented', type: 'completion', text: 'Present to class' },
      { id: 'certificates-issued', type: 'completion', text: 'Receive certificates and celebrate' },
    ],
  },
];

const indexById = (id: string) => LEVELS.findIndex(l => l.id === id);

const getLevelStatus = (levelId: string, currentLevelId: string, completedIds: string[]): LevelStatus => {
  if (completedIds.includes(levelId)) return 'completed';
  if (levelId === currentLevelId) return 'next';
  return 'upcoming';
};

const getShortLevelName = (title: string) => {
  return title.length > 12 ? `${title.slice(0, 12)}…` : title;
};

const getMissionActivity = (missionId: string) => {
  // Mission-specific activity cards for Level 1: "Muses & Worlds"
  // Each entry provides concise, step-by-step instructions aligned with the student worksheets.
  const entries: Record<string, any> = {
    // 0) Welcome & Overview
    'welcome-overview': {
      title: 'Welcome & Overview',
      description: 'Safety workflow and today\'s goal: create a Muse and a World. AI follows your words—be clear and safe.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        'Projector (Canva board only)',
        'Class 1 Worksheets',
        { label: 'Gemini', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      instructions: [
        'Share the mission: “Create a Muse and a World with AI. Your words control the AI.”',
        'Explain the workflow: write in workbook → teacher reviews → teacher types in Gemini → reflect.',
        'Set expectations: complete sentences, safe/age-appropriate prompts, no personal info.',
      ],
    },

    // 1) Warm-up RealOrAI
    'realorai-l1': {
      title: 'Warm-Up: RealOrAI',
      description: 'Play a short round and connect prompting ideas to outputs and warm up creative vocabulary.',
      duration: '10 minutes',
      difficulty: 'Easy',
      materials: ['overturegames.org (RealOrAI)'],
      image: RealOrAiThumb,
      link: '/games/RealOrAI/index.html',
      instructions: [
        'Play 1 quick round together and discuss why an image might be real vs AI.',
        'Introduce “prompt → result” idea: clearer words = clearer images.',
        'Learn names; ask favorite ice cream; ask what students know about AI.',
      ],
    },

    // 2) Muse Level 1
    'muse-image-l1': {
      title: 'Muse L1: Describe Your Muse',
      description: 'Students define animal, name, pose, and mood. Teacher prompts Gemini.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        { label: 'Class 1 Worksheet (Muse + World)', link: 'https://www.canva.com/design/DAG13QOCn28/t6NDGLqqIIr-FEo8vOxEvw/edit?utm_content=DAG13QOCn28&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        { label: 'Gemini (nano-banana)', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      image: MuseL1Img,
      instructions: [
        'Students fill worksheet blanks: Animal, Name, Activity/Pose, Mood.',
        'Teacher opens a fresh Gemini chat (nano-banana).',
        'Type a complete prompt using student choices (example): “Make an image of my Muse. My Muse is a zebra named Zed. Zed is dancing and happy.”',
        'Paste/save the best image to the Canva board as Muse L1.',
      ],
    },

    // 3) Muse Level 2
    'muse-accessories-l2': {
      title: 'Muse L2: Dress to Impress',
      description: 'Add clothing with color and material to the same Muse.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        { label: 'Class 1 Worksheet (Muse + World)', link: 'https://www.canva.com/design/DAG13QOCn28/t6NDGLqqIIr-FEo8vOxEvw/edit?utm_content=DAG13QOCn28&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        { label: 'Gemini', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      image: MuseL2Img,
      instructions: [
        'Students choose clothing item(s), color, and material on worksheet.',
        'If results drift, start a new chat and paste the last best image.',
        'Prompt (example): “Zed is wearing a blue cotton bucket hat and green rubber sneakers.”',
        'Save and paste result as Muse L2 on Canva.',
      ],
    },

    // 4) Muse Level 3
    'muse-secret-items-l3': {
      title: 'Muse L3: Secret Items',
      description: 'Add an item and explicit placement (left/right hand, on back, etc.).',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        { label: 'Class 1 Worksheet (Muse + World)', link: 'https://www.canva.com/design/DAG13QOCn28/t6NDGLqqIIr-FEo8vOxEvw/edit?utm_content=DAG13QOCn28&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        { label: 'Gemini', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      image: MuseL3Img,
      instructions: [
        'Students pick item(s), color/material, and location on the body.',
        'Prompt (example): “Zed has a paintbrush in his left hand.”',
        'Confirm correct side/placement; retry if orientation is wrong.',
        'Save and paste as Muse L3 on Canva.',
      ],
    },

    // 5) World Level 1
    'world-base-l1': {
      title: 'World L1: Describe Your Landscape',
      description: 'Name your world and define weather, land, and mood.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        { label: 'Class 1 Worksheet (Muse + World)', link: 'https://www.canva.com/design/DAG13QOCn28/t6NDGLqqIIr-FEo8vOxEvw/edit?utm_content=DAG13QOCn28&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        { label: 'Gemini', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      image: WorldL1Img,
      instructions: [
        'Students choose Weather, Name, Land (mountains/forest/desert), and Mood.',
        'Prompt (example): “Make an image of my landscape called Aria. Aria is a windy, spooky desert.”',
        'Save and paste as World L1 on Canva.',
      ],
    },

    // 6) World Level 2
    'world-flora-fauna-l2': {
      title: 'World L2: Plants & Animals',
      description: 'Add flora, fauna, and one landform to your world.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        { label: 'Class 1 Worksheet (Muse + World)', link: 'https://www.canva.com/design/DAG13QOCn28/t6NDGLqqIIr-FEo8vOxEvw/edit?utm_content=DAG13QOCn28&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        { label: 'Gemini', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      image: WorldL2Img,
      instructions: [
        'Students list 2 plants, 1 animal, and 1 landform on worksheet.',
        'Prompt (example): “Add capybaras, cactuses, cherry trees, and a valley to Aria.”',
        'Save and paste as World L2 on Canva.',
      ],
    },

    // 7) World Level 3
    'world-buildings-l3': {
      title: 'World L3: Building',
      description: 'Add a structure with 3–4 descriptors and a position.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        { label: 'Class 1 Worksheet (Muse + World)', link: 'https://www.canva.com/design/DAG13QOCn28/t6NDGLqqIIr-FEo8vOxEvw/edit?utm_content=DAG13QOCn28&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        { label: 'Gemini', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      image: WorldL3Img,
      instructions: [
        'Students pick a building and write 3–4 descriptors (size, shape, color, material). Add position (left/right/on hill).',
        'Prompt (example): “Add a large, round, orange, creaky house to the right side of Aria.”',
        'Save and paste as World L3 on Canva.',
      ],
    },

    // 8) World Level 4
    'world-collab-l4': {
      title: 'World L4: Make a Friend (Collab)',
      description: 'Combine two Muse images into one shared world image.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        { label: 'Class 1 Worksheet (Muse + World)', link: 'https://www.canva.com/design/DAG13QOCn28/t6NDGLqqIIr-FEo8vOxEvw/edit?utm_content=DAG13QOCn28&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        'Muse A image',
        'Muse B image',
        'World image',
        { label: 'Gemini', link: 'https://gemini.google.com/app' },
        { label: 'Canva Whiteboard', link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' }
      ],
      image: WorldL4Img,
      instructions: [
        'Pair students. Gather Muse A, Muse B, and the World image.',
        'Paste the three images into Gemini along with a written prompt.',
        'Prompt (example): “Add the images of Zed and Jill to the landscape of Aria. Zed and Jill are dancing on the roof.”',
        'Save and paste as World L4 (Collab) on Canva.',
      ],
    },
  };

  const base = {
    id: `ai-fall-2025-${missionId}`,
    image: '',
    link: undefined,
    materials: [],
  } as any;

  const entry = entries[missionId];
  if (entry) return { ...base, ...entry } as any;

  // Fallback placeholder for missions not yet authored
  return {
    ...base,
    title: `AI Fall 2025 Activity: ${missionId}`,
    description: 'Activity placeholder. Will be replaced with specific links/assets later.',
    duration: '5-15 minutes',
    difficulty: 'Easy',
    instructions: [],
  } as any;
};

export const aiFall2025Adapter: CurriculumAdapter = {
  id: 'ai-fall-2025',
  levels: LEVELS,
  getLevelById: (id: string) => LEVELS.find(l => l.id === id),
  getLevelIndex: (id: string) => Math.max(0, indexById(id)),
  getLevelStatus,
  getShortLevelName,
  getMissionActivity,
};
